import { apiClient } from './client';

export interface ShipBobTokenStatus {
  valid: boolean;
  last_refreshed_at: string | null;
}

export type ShipBobConfigData = Record<string, unknown>;

/** Laravel API Resource wraps a single resource in a top-level `data` key. */
export interface ShipBobConfigResponse {
  data: ShipBobConfigData;
}

export interface ShipBobEndpointField {
  /** Query param name sent to the backend. */
  key: string;
  label: string;
  /** When true, the input is parsed as a comma-separated list and sent as an array. */
  multiple?: boolean;
  /** Short explanation shown under the input — `returnId`/`referenceId(s)` mean different things per endpoint. */
  hint?: string;
}

export interface ShipBobAllowedEndpoint {
  value: string;
  label: string;
  fields: ShipBobEndpointField[];
}

const LOOP_RETURN_ID_LOG_ONLY_HINT = "Loop's own return ID. Used for logging only — not sent to ShipBob for this call.";

/**
 * Mirrors the backend's ShipBobAllowedEndpoint enum and the params each fetcher needs (GET-only allowlist).
 * Field keys are camelCase to match the backend's validation field names. Labels/hints reflect the real
 * meaning of each param confirmed against `ShipBobClient`/`ShipBobService` — `returnId` is Loop's return ID
 * everywhere, but `referenceId(s)` means something different per endpoint, and `shipBobReturnId` (Return only)
 * is the one genuinely ShipBob-side ID.
 */
export const SHIPBOB_ALLOWED_ENDPOINTS: ShipBobAllowedEndpoint[] = [
  {
    value: 'order',
    label: 'Order',
    fields: [
      { key: 'returnId', label: 'Loop Return ID', hint: LOOP_RETURN_ID_LOG_ONLY_HINT },
      {
        key: 'referenceId',
        label: 'Provider Order ID',
        hint: "The order's provider_order_id (e.g. Shopify order ID), sent as ShipBob's ReferenceIds filter on /order.",
      },
    ],
  },
  {
    value: 'product',
    label: 'Product',
    fields: [
      { key: 'returnId', label: 'Loop Return ID', hint: LOOP_RETURN_ID_LOG_ONLY_HINT },
      {
        key: 'referenceIds',
        label: 'SKU / Variant Reference IDs (comma separated)',
        hint: "Loop-side product identifiers, sent as ShipBob's ReferenceIds filter on /product.",
        multiple: true,
      },
    ],
  },
  {
    value: 'inventory',
    label: 'Inventory',
    fields: [
      { key: 'returnId', label: 'Loop Return ID', hint: LOOP_RETURN_ID_LOG_ONLY_HINT },
      {
        key: 'ids',
        label: 'ShipBob Inventory Item IDs (comma separated)',
        hint: 'From a prior Product lookup (fulfillableInventoryItems[].id).',
        multiple: true,
      },
    ],
  },
  {
    value: 'return',
    label: 'Return',
    fields: [
      { key: 'returnId', label: 'Loop Return ID', hint: LOOP_RETURN_ID_LOG_ONLY_HINT },
      {
        key: 'shipBobReturnId',
        label: 'ShipBob Return ID',
        hint: "ShipBob's own Return Order ID — drives the /return/{id} URL directly.",
      },
    ],
  },
  {
    value: 'returns',
    label: 'Returns',
    fields: [
      {
        key: 'returnId',
        label: 'Loop Return ID',
        hint: "Sent as ShipBob's ReferenceIds filter on /return (ShipBob's reference_id was set to this at creation time).",
      },
      {
        key: 'channelId',
        label: 'ShipBob Channel ID',
        hint: "This shop's Loop Returns channel ID in ShipBob.",
      },
    ],
  },
  {
    value: 'channel',
    label: 'Channels',
    fields: [],
  },
  {
    value: 'location',
    label: 'Fulfillment Centers',
    fields: [],
  },
];

export function getStatus (shopId: number | string) {
  return apiClient.get<ShipBobTokenStatus>(`/${shopId}/shipbob/status`);
}

export function getConfig (shopId: number | string) {
  return apiClient.get<ShipBobConfigResponse>(`/${shopId}/shipbob/config`);
}

export function getLogs (shopId: number | string, timeRange: string) {
  return apiClient.get(`/${shopId}/shipbob/logs/${timeRange}`);
}

export function callEndpoint (shopId: number | string, endpoint: string, params: Record<string, string | string[]>) {
  return apiClient.get(`/${shopId}/shipbob/call/${endpoint}`, { params });
}
